import { inject, Injectable } from "@angular/core";
import { ModalService } from "@app/components/modal/modal.service";
import { DiroResponseDto, DiroStatus } from "@app/pages/quote/open-quote/model/open-quote.model";
import { ApiService } from "@app/services/api.base.service";
import { environment } from "@env/environment.local";
import { Client } from "@stomp/stompjs";
import { Subject, Observable, from, concatMap, of, delay } from "rxjs";
import responseDiro from '../../../../../assets/json/ResponseDiro.json';
import responseDeleteDiro from '../../../../../assets/json/ResponseDeleteDiro.json';
import * as SockJS from "sockjs-client";
import { FormGroup } from "@angular/forms";

const UPM = "upm";
const UPP = "upp";
const BASE_INFORMATION = "baseInformation";

export interface DiroEvent {
  type: 'status_update' | 'response_received' | 'error' | 'modal_required';
  data?: any;
}

export interface DiroModalRequirement {
  type: 'status' | 'login' | 'confirmation';
  data?: any;
}

@Injectable({
  providedIn: 'root',
})
export class DiroIntegrationService {
  private readonly api = inject(ApiService);
  private readonly modalService = inject(ModalService);

  public readonly testDiro = environment.testDiro;
  public readonly activeDiro = environment.activarDiro;
  public  readonly UPM = UPM;
  public readonly UPP = UPP;
  public readonly BASE_INFORMATION = BASE_INFORMATION;

  private client: Client;
  private idPetition = -1;
  private statusDiroAnt: DiroStatus;
  public statusDiro: DiroStatus;

  public diroResponseDto: DiroResponseDto;
  public idQuoteDiro: any;
  public idDealDiro: any;
  public othersDiro: any;
  public baseInfoDiro: any;
  public modelDiro: any;

  /**
   * Emite cuando hay un cambio de estado en DIRO
   */
  private readonly statusChangeSubject = new Subject<DiroStatus>();
  public readonly statusChange$: Observable<DiroStatus> = this.statusChangeSubject.asObservable();

  /**
   * Emite cuando se recibe una respuesta completa de DIRO
   */
  private readonly responseReceivedSubject = new Subject<any>();
  public readonly responseReceived$: Observable<any> = this.responseReceivedSubject.asObservable();

  /**
   * Emite cuando se necesita abrir un modal
   * El componente debe suscribirse y abrir el modal apropiado
   */
  private readonly modalRequiredSubject = new Subject<DiroModalRequirement>();
  public readonly modalRequired$: Observable<DiroModalRequirement> = this.modalRequiredSubject.asObservable();

  /**
   * Emite cuando hay un error en DIRO
   */
  private readonly errorSubject = new Subject<string>();
  public readonly error$: Observable<string> = this.errorSubject.asObservable();

  /**
   * Estado actual observable (para debugging/monitoring)
   */
  private readonly connectionStateSubject = new Subject<'connected' | 'disconnected' | 'connecting' | 'error'>();
  public readonly connectionState$: Observable<string> = this.connectionStateSubject.asObservable();

  connect(): void {
    if (!this.activeDiro || this.testDiro) {
      console.warn('DIRO is disabled or in test mode');
      return;
    }

    this.connectionStateSubject.next('connecting');

    this.client = new Client({
      webSocketFactory: () => {
        return {} as WebSocket;
      },
      onConnect: (frame) => {
        this.subscribeToMessages();
        this.connectionStateSubject.next('connected');
      },
      onDisconnect: () => {
        this.connectionStateSubject.next('disconnected');
      },
      onStompError: (error) => {
        console.error('STOMP error:', error);
        this.connectionStateSubject.next('error');
        this.errorSubject.next('Error de conexión con DIRO');
      }
    });

    this.client.activate();
  }

  disconnect(): void {
    if (this.client) {
      this.client.deactivate();
    }

    sessionStorage.removeItem('lastInput');
  }

  getStatusModalData(): any {
    if (this.statusDiro.data?.id && this.idPetition === this.statusDiro.data.id) {
      if (this.statusDiro && String(this.statusDiro.pcGeneral) !== '18') {
        return {
          title: 'Sincronizando datos con Diro.',
          subtitle: 'Fase:' + this.statusDiro.fase + '\n',
          text: `Porcentaje de avance "${this.statusDiro.pcGeneral}"%`,
        };
      } else {
        return {
          title: 'Código doble autenticación.',
          subtitle: 'Código:' + this.statusDiro.fase + '\n',
          text: `Porcentaje de avance "${this.statusDiro.pcGeneral}"%`,
        };
      }
    } else if (this.statusDiro.data?.id && this.idPetition !== -1) {
      return {
        title: 'Diro procesando otras peticiones.',
        subtitle: 'Tu petición es la ' + this.idPetition + '\n',
        text: 'Faltan ' + (this.idPetition - this.statusDiro.data.id),
      };
    }

    return {
      title: '',
      subtitle: '',
      text: '',
    };
  }

  getLastResponse(): DiroResponseDto | null {
    return this.diroResponseDto || null;
  }

  getOtherDiroDto(formGroup: FormGroup) {
    let grace = formGroup.controls['grace'].value;
    let special = formGroup.controls['specialPayments'].value;
    let irregular = formGroup.controls['irregularPayments'].value;

    return {
      specialPaymentsGrace: this.processGraceSpecialPayments(grace, special),
      dealName: formGroup.controls['dealName'].value,
      classification: formGroup.controls['classification'].value,
      opexSegment: formGroup.controls['opexSegment'].value,
      productType: formGroup.controls['productType'].value,
      useTime: formGroup.controls['useTime'].value,
      unitPriceWithoutTax: formGroup.controls['unitPriceWithoutTax'].value,
      subsidyType: formGroup.controls['subsidyType'].value,
      subsidyPercentage: formGroup.controls['subsidyPercentage'].value,
      openingFeeWithTax: formGroup.controls['openingFeeWithTax'].value,
      openingFeeWithoutTax:
        formGroup.controls['openingFeeWithoutTax'].value,
      guaranteeDepositAmount:
        formGroup.controls['result'].value.guaranteeDepositAmount,
      purchaseTotal: formGroup.controls['purchaseTotal'].value,
      paymentType: formGroup.controls['paymentType'].value,
      residualValueTotal: formGroup.controls['residualValueTotal'].value,
      idQuoteDiro: this.idQuoteDiro,
      idDealDiro: this.idDealDiro,
    }
  }

  startDiroOperations(
    model: any,
    baseInformation: any,
    usr: any,
    others: any
  ) {
    var input = {
      ...model,
      ...baseInformation,
      ...usr,
      ...others,
    };
    const cloneInput = JSON.parse(JSON.stringify(input));
    let lastInputStr = sessionStorage.getItem('lastInput');
    let lastInput = lastInputStr ? JSON.parse(lastInputStr) : null;
    if (lastInput == null)
      sessionStorage.setItem('lastInput', JSON.stringify(input));
    else input = this.processLastInput(input, lastInput);
    this.statusDiro.fase = ' Inicial';
    this.statusDiro.pcGeneral = '0';
    this.statusDiro.idPetition = -1;
    this.statusDiro.data = { id: -1 };

    this.modalRequiredSubject.next({
      type: 'status',
      data: this.getStatusModalData()
    });

    if (Object.keys(input).length !== 0) {
      if (input.idQuoteDiro == undefined && lastInput && lastInput.idQuoteDiro)
        input.idQuoteDiro = lastInput.idQuoteDiro;
      if (input.idDealDiro == undefined && lastInput && lastInput.idDealDiro)
        input.idDealDiro = lastInput.idDealDiro;
      input.upp = usr.upp;
      input.upm = usr.upm;
      sessionStorage.setItem('lastInput', JSON.stringify(cloneInput));
      this.getFromDiro(input).subscribe({
        next: (response) => {
          if (response.state && response.state == 'ok') {
            this.diroResponseDto = new DiroResponseDto(response.data);
            this.idDealDiro = this.diroResponseDto.idDeal;
            this.idQuoteDiro = this.diroResponseDto.idQuote;
            this.idPetition = response.data;
            this.responseReceivedSubject.next({ setQuote: true, data: response });
          }
        },
        error: (error) => {
          this.modalService.closeModal();
        },
      });
    }

  }

  cancelDiroOperation(deal: any) {
    let calculateDiro = false;
    if (this.activeDiro) {
      if (deal.idQuote == undefined || deal.idQuote == null) {
        if (this.diroResponseDto) {
          let data = {
            idDeal: this.diroResponseDto.idDeal,
            idQuote: this.diroResponseDto.idQuote,
          };
          this.deleteDiroQuote(data);
        }
      } else {
        if (this.diroResponseDto && deal) {
          calculateDiro = true;
        }
      }
    }
    sessionStorage.removeItem('lastInput');
    return { calculateDiro, deal };
  }

  private processGraceSpecialPayments(grace: any[], special: any[]): any[] {
    let out: any[] = [];
    grace.forEach((element) => {
      out.push({
        nuMonth: element.period,
        type: this.getType(element),
        value: 0.0,
      });
    });
    special.forEach((element) => {
      out.push({
        nuMonth: element.period,
        type: this.getType(element),
        value: parseFloat(element.amount + ''),
      });
    });
    return out;
  }

  private getType(element: any) {
    let typeOut = '';
    switch (element.paymentType) {
      case 'GRACE_PERIOD':
        switch (element.applyTo) {
          case 'TOTAL':
            typeOut = 'No Regular Payment';
            break;
          case 'CAPITAL':
            typeOut = 'Principal Only';
            break;
          case 'INTEREST':
            typeOut = 'Interest Only';
            break;
          default:
            break;
        }
        break;
      case 'SPECIAL_PAYMENT':
        switch (element.applyTo) {
          case 'CAPITAL':
            typeOut = 'Principal Only';
            break;
          default:
            break;
        }
        break;
      default:
        break;
    }
    return typeOut;
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  private deepDiff(obj1: any, obj2: any): any {
    if (this.isObject(obj1) && this.isObject(obj2)) {
      const diff: Record<string, any> = {};
      const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);
      keys.forEach((key) => {
        const value1 = obj1[key];
        const value2 = obj2[key];
        if (this.isObject(value1) && this.isObject(value2)) {
          const nestedDiff = this.deepDiff(value1, value2);
          if (Object.keys(nestedDiff).length > 0) {
            diff[key] = nestedDiff;
          }
        } else if (Array.isArray(value1) && Array.isArray(value2)) {
          if (JSON.stringify(value1) !== JSON.stringify(value2)) {
            diff[key] = value1;
          }
        } else if (value1 !== value2) {
          diff[key] = value1;
        }
      });
      return diff;
    } else {
      return obj1 !== obj2 ? obj1 : {};
    }
  }

  private processLastInput(
    input: Record<string, any>,
    lastInput: Record<string, any>
  ): Record<string, any> {
    return this.deepDiff(input, lastInput);
  }

  private subscribeToMessages(): void {
    this.client.subscribe('/topic/serv1', (message) => {
      try {
        this.handleDiroMessage(message.body);
      } catch (e) {
        console.error('Error processing DIRO message:', e);
        this.errorSubject.next('Error procesando mensaje de DIRO');
      }
    });
  }

  private handleDiroMessage(messageBody: string): void {
    this.modalService.closeModal();
    this.statusDiroAnt = this.statusDiro;
    this.statusDiro = JSON.parse(messageBody);
    this.statusChangeSubject.next(this.statusDiro);

    if (this.idPetition === -1) {
      return;
    }

    // Manejar excepciones
    if (this.hasException()) {
      this.handleExceptionCase();
      return;
    }

    // Manejar progreso
    if (this.isInProgress()) {
      // Emitir que se necesita mostrar el modal de estado
      this.modalRequiredSubject.next({
        type: 'status',
        data: this.getStatusModalData()
      });
    }else if (this.isCompleted()) {
      this.handleCompletion();
    }
  }

  private hasException(): boolean {
    return this.statusDiro.fase === 'Exception' &&
           this.statusDiro.data?.id === this.idPetition;
  }

  private isInProgress(): boolean {
    return this.statusDiro.pcGeneral !== '100' &&
           this.statusDiro.pcGeneral !== null;
  }

  private isCompleted(): boolean {
    return this.statusDiro.pcGeneral === '100';
  }

  private handleExceptionCase(): void {
    sessionStorage.removeItem('lastInput');

    const isAuthError = this.statusDiroAnt &&
                       this.statusDiroAnt.pcGeneral &&
                       (this.statusDiroAnt.pcGeneral === '15' ||
                        this.statusDiroAnt.pcGeneral === '18');

    if (isAuthError) {
      window.sessionStorage.removeItem(UPP);
      window.sessionStorage.removeItem(UPM);
      this.errorSubject.next(
        'Ha ocurrido un error al enviar a Diro, usuario o contraseña con error.'
      );
    } else {
      this.errorSubject.next(
        'Ha ocurrido un error al enviar a Diro, envia de nuevo o\n revisa en Diro los detalles.'
      );
    }

    this.idPetition = -1;
  }

  private handleCompletion(): void {
    const response = this.statusDiro.data.out;
    this.diroResponseDto = new DiroResponseDto(response?.data);
    this.idDealDiro = this.diroResponseDto.idDeal;
    this.idQuoteDiro = this.diroResponseDto.idQuote;

    this.idPetition = -1;
    this.responseReceivedSubject.next({ setQuote: false, data: response });
  }

  private getFromDiro(data?: any): Observable<any> {
    if (environment.testDiro) {
      let test: any = [responseDiro];
      return from(test).pipe(concatMap((item) => of(item).pipe(delay(30000))));
    }
    return this.api.post(`${environment.API_DIRO_SERVICE}/diroRorac`, data);
  }

  private deleteDiroQuote(data?: any): Observable<any> {
    if (environment.testDiro) {
      let test: any = [responseDeleteDiro];
      return from(test).pipe(concatMap((item) => of(item).pipe(delay(1000))));
    }
    return this.api.delete(`${environment.API_DIRO_SERVICE}/deleteQuote`, data);
  }

  ngOnDestroy(): void {
    this.statusChangeSubject.complete();
    this.responseReceivedSubject.complete();
    this.modalRequiredSubject.complete();
    this.errorSubject.complete();
    this.connectionStateSubject.complete();

    this.disconnect();
  }
}
